import BaseController, { INVITE_VALUE_STATE_NONE, type InviteValueState } from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type Select from "sap/m/Select";
import type TextArea from "sap/m/TextArea";
import type Text from "sap/m/Text";
import type FileUploader from "sap/ui/unified/FileUploader";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";
import { showInviteOutcome, type InviteUserResponse } from "../util/InviteOutcome";

interface LicenseSummary {
	Id: string;
	EntitlementId: string;
	LicenseType: string;
	Quantity: number;
	Status: string;
	ValidFrom: string;
	ValidUntil: string;
	// Enriched z /odata/v4/EntitlementReports (po EntitlementId), żeby pokazać produkt
	// zamiast samego GUID-a. Pola opcjonalne — jeśli fetch entitlement-reports padnie,
	// pokazujemy "—" zamiast wywalać całą sekcję licencji.
	ProductId?: string;
	ProductName?: string;
	ProductCode?: string;
	BillingMode?: string;
	LicensingMode?: string;
	// ADR-014 Slice 6: enriched z /odata/v4/LicenseAssignments (Status=Active, joined po LicenseId).
	AssignedUserId?: string;
	AssignedUserEmail?: string;
}

/**
 * Seat-pool aggregation: grupowanie InPortal licencji per (ProductId, LicenseType).
 * Budowane klient-side w <c>buildLicenseGroups</c> po wszystkich enrichmentach.
 * Tylko grupy gdzie <c>licensingMode === 'InPortal'</c> trafiają do view modelu —
 * Out-of-Portal pozostaje w starej flat tabeli (inny model: pojedyncze pliki, brak puli).
 */
interface LicenseGroupAssignment {
	licenseId: string;
	assignmentId: string;
	userId: string;
	userEmail: string;
	userFullName: string;
	assignedAt: string;
}

interface LicenseGroup {
	key: string;                       // `${productId}|${licenseType}`
	productId: string;
	productCode: string;
	productName: string;
	licenseType: string;
	licensingMode: string;
	entitlementId: string;             // EntitlementId puli — używane przy "Invite user" do auto-assign
	totalSeats: number;
	assignedSeats: number;
	freeSeats: number;
	emptySeatLicenseIds: string[];     // licenses w grupie ze Status='EmptySeat' — używane przy Assign
	assignments: LicenseGroupAssignment[];
}

interface AssignableUser {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
}

interface EntitlementReportSummary {
	Id: string;
	ProductId: string;
	ProductCode: string;
	ProductName: string;
	BillingMode: string;
}

/** One choice in the change-owner dialog; `key` is a user id or {@link NO_OWNER_KEY}. */
interface OwnerCandidate {
	key: string;
	text: string;
	additionalText: string;
}

/** A row of `GET /api/v1/organizations/{id}/users` (UserEndpoints.PortalUserListItem). */
interface OrganizationUserRow {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: string | null;
	status: string;
}

/** Select key of the "no owner" choice; it is sent as `ownerUserId: null`. */
const NO_OWNER_KEY = "none";

interface ViewState {
	deploymentId: string;
	ownerId: string;
	ownerName: string;
	// Card 50: the employee who runs the deployment; empty when nobody does or the caller is not CompuTec.
	ownerUserId: string;
	ownerUserDisplayName: string;
	changeOwner: {
		candidates: OwnerCandidate[];
		selectedKey: string;
		// Key of the owner the deployment has now; Save stays disabled while the selection equals it.
		currentKey: string;
		busy: boolean;
	};
	licenses: LicenseSummary[];               // wszystkie licencje (do KPI)
	licensesOOP: LicenseSummary[];            // tylko OutOfPortal — flat tabela w drugiej subsekcji
	licenseGroupsInPortal: LicenseGroup[];   // pulle seatów InPortal — render w nowej subsekcji
	hasOutOfPortal: boolean;                  // visibility flagi starej flat tabeli
	kpi: {
		activeLicenses: string;
	};
	assignLicenseId: string;
	assignLicenseLabel: string;
	assignGroupKey: string;                   // klucz grupy z której assignujemy (do refresh po sukcesie)
	assignableUsers: AssignableUser[];
	assignSelectedUserId: string;

	// InviteUserDialog state (fragment reuse z PortalUsers feature).
	// Dla DeploymentDetail org picker UKRYTY (inviteShowOrgPicker=false), bo zawsze
	// invitujemy do customera tego deploymentu (inviteOrgId=ownerId).
	inviteShowOrgPicker: boolean;
	inviteOrgId: string;
	inviteOrgName: string;
	inviteCompuTecOrgId: string;
	inviteHasPortalAccess: boolean;
	inviteRoleIsUser: boolean;
	inviteValueState: InviteValueState;

	// Auto-assignment intent przy invite z kontekstu LicenseGroup. Pusty = klasyczny invite
	// bez auto-assign (tak działa "Invite user" gdy klikniesz globalnie poza pulą).
	// `inviteIntendedEntitlementLabel` jest tylko do wyświetlenia w dialogu ("Auto-assign to: Luna AI — Pro").
	inviteIntendedEntitlementId: string;
	inviteIntendedEntitlementLabel: string;

	// Live runtime state — ładowane asynchronicznie z /api/v1/deployments/{id}/runtime-status.
	// `loaded` = false dopóki fetch nie wróci (sukces lub błąd); sekcja Runtime pokazuje
	// MessageStrip "niedostępne" gdy loaded=true && available=false.
	runtime: {
		loaded: boolean;
		available: boolean;
		outcome: string;
		lastHeartbeatAt: string | null;
		mustHeartbeatBy: string | null;
		healthState: string;
		healthText: string;
	};
	environment: {
		loaded: boolean;
		available: boolean;
		data: unknown;
	};

	// Update ceiling per product family (card 26) — how far this deployment may update each family.
	// `loaded` stays false until the fetch settles; the row text is resolved here rather than by an
	// XML formatter, the same way runtime.healthText is, so i18n lookup lives in one place.
	// `failed` separates "the request did not come back" from "no family is bound here": the empty
	// text is a claim about the data, and rendering it for a 500 would state the opposite of the
	// truth to an operator deciding whether a customer may update.
	updateCeilings: {
		loaded: boolean;
		failed: boolean;
		rows: UpdateCeilingRow[];
	};
}

interface UpdateCeilingRow {
	productFamilyId: string;
	productFamilyCode: string;
	productFamilyName: string;
	updateAllowedUntil: string | null;
	updateAllowedUntilText: string;
}

interface IssueEnrollmentCodeResponse {
	code: string;
	expiresAt: string;
}

/**
 * @namespace computec.b2b.portal.controller
 */
export default class DeploymentDetail extends BaseController {
	private codeDialog?: Dialog;
	private reEnrollDialog?: Dialog;
	private resetDialog?: Dialog;
	private offlineEnrollDialog?: Dialog;
	private offlineRefreshDialog?: Dialog;
	private offlineBundleDialog?: Dialog;
	private changeOwnerDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			deploymentId: "",
			ownerId: "",
			ownerName: "",
			ownerUserId: "",
			ownerUserDisplayName: "",
			changeOwner: { candidates: [], selectedKey: NO_OWNER_KEY, currentKey: NO_OWNER_KEY, busy: false },
			licenses: [],
			licensesOOP: [],
			licenseGroupsInPortal: [],
			hasOutOfPortal: false,
			kpi: { activeLicenses: "—" },
			assignLicenseId: "",
			assignLicenseLabel: "",
			assignGroupKey: "",
			assignableUsers: [],
			assignSelectedUserId: "",
			inviteShowOrgPicker: false,
			inviteOrgId: "",
			inviteOrgName: "",
			inviteCompuTecOrgId: "",
			inviteHasPortalAccess: false,
			inviteRoleIsUser: true,
			inviteValueState: { ...INVITE_VALUE_STATE_NONE },
			inviteIntendedEntitlementId: "",
			inviteIntendedEntitlementLabel: "",
			runtime: {
				loaded: false,
				available: false,
				outcome: "",
				lastHeartbeatAt: null,
				mustHeartbeatBy: null,
				healthState: "None",
				healthText: ""
			},
			environment: {
				loaded: false,
				available: false,
				data: null
			},
			updateCeilings: {
				loaded: false,
				failed: false,
				rows: []
			}
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getRouter().getRoute("deploymentDetail")?.attachPatternMatched((e) => {
			this.onPatternMatched(e);
		});
	}

	private onPatternMatched(event: Route$PatternMatchedEvent): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/deploymentId", id);

		this.getView().bindElement({
			path: `/Deployments(${id})`,
			model: "odata"
		});

		void this.loadOwner(id);
		void this.loadLicenses(id);
		void this.loadRuntimeStatus(id);
		void this.loadEnvironment(id);
		void this.loadUpdateCeilings(id);
	}

	/**
	 * Fetches the update ceiling of every product family bound on this deployment (card 26).
	 * A null ceiling means the family imposes none — nothing maintenance-bearing is bound — which is
	 * not the same as "updates forbidden", so it is rendered as its own label rather than as a blank.
	 * A failed request does not block the rest of the page, but it must not be rendered as an empty
	 * result either: "no family is bound here" is a statement about the data, and showing it after a
	 * 500 would tell the operator the opposite of the truth.
	 */
	private async loadUpdateCeilings(deploymentId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;

		// Reset synchronously before the first await so navigating A→B never shows A's ceilings.
		view.setProperty("/updateCeilings", { loaded: false, failed: false, rows: [] });

		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const [bundle, rows] = await Promise.all([
				this.getResourceBundle(),
				fetcher.json<UpdateCeilingRow[]>(`/api/v1/deployments/${deploymentId}/update-ceilings`)
			]);
			const noCeiling = bundle.getText("deploymentDetail.updateCeiling.none") ?? "";
			view.setProperty("/updateCeilings", {
				loaded: true,
				failed: false,
				rows: rows.map((row) => ({
					...row,
					updateAllowedUntilText: row.updateAllowedUntil ?? noCeiling
				}))
			});
		} catch (e) {
			console.error("[DeploymentDetail] update ceilings failed to load", e);
			view.setProperty("/updateCeilings", { loaded: true, failed: true, rows: [] });
		}
	}

	/**
	 * Fetches the latest environment observation (AE version, fingerprint quality, SAP ids, ...) from LR
	 * via the portal backend. Always HTTP 200 — available:false when there is no telemetry yet or LR is down.
	 */
	private async loadEnvironment(deploymentId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/environment", { loaded: false, available: false, data: null });
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const res = await fetcher.json<{ available: boolean; environment: unknown }>(
				`/api/v1/deployments/${deploymentId}/environment`);
			view.setProperty("/environment", { loaded: true, available: res.available, data: res.environment ?? null });
		} catch {
			view.setProperty("/environment", { loaded: true, available: false, data: null });
		}
	}

	private async loadOwner(deploymentId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		// Reset before the first await so navigating A→B never offers A's owner in B's dialog.
		view.setProperty("/ownerUserId", "");
		view.setProperty("/ownerUserDisplayName", "");
		try {
			// The owner name travels on the deployment itself (OwnerOrgName), so this page needs no
			// read of the Organizations feed - which a caller without orgs.read would get 403 for.
			const dep = await fetcher.json<{
				OwnerOrgId?: string;
				OwnerOrgName?: string;
				OwnerUserId?: string | null;
				OwnerUserDisplayName?: string | null;
			}>(
				`/odata/v4/Deployments(${deploymentId})?$select=OwnerOrgId,OwnerOrgName,OwnerUserId,OwnerUserDisplayName`,
				{ headers: { Accept: "application/json" } }
			);
			view.setProperty("/ownerUserId", dep.OwnerUserId ?? "");
			view.setProperty("/ownerUserDisplayName", dep.OwnerUserDisplayName ?? "");
			if (!dep.OwnerOrgId) {
				return;
			}
			view.setProperty("/ownerId", dep.OwnerOrgId);
			if (dep.OwnerOrgName) {
				view.setProperty("/ownerName", dep.OwnerOrgName);
			}
		} catch {
			// silent fail — header KPI po prostu pokaże "—"
			view.setProperty("/ownerUserId", "");
			view.setProperty("/ownerUserDisplayName", "");
		}
	}

	/**
	 * Fetches live runtime state from LR via the portal backend proxy.
	 * Always HTTP 200 — `available:false` when LR is down. On network error falls back
	 * gracefully: local fields (from OData) remain visible; MessageStrip "unavailable" shows.
	 *
	 * Heartbeat health thresholds:
	 *   - no data / LR down  → None  (deploymentDetail.runtime.health.none)
	 *   - now > mustHeartbeatBy → Error (deploymentDetail.runtime.health.overdue)
	 *   - deadline − now < 24h  → Warning (deploymentDetail.runtime.health.due)
	 *   - else                  → Success (deploymentDetail.runtime.health.healthy)
	 */
	private async loadRuntimeStatus(deploymentId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;

		// Fix: reset synchronously before any await so navigating A→B never flashes A's
		// runtime state while B's fetch is in flight.
		view.setProperty("/runtime", {
			loaded: false,
			available: false,
			outcome: "",
			lastHeartbeatAt: null,
			mustHeartbeatBy: null,
			healthState: "None",
			healthText: ""
		});

		const fetcher = this.getOwnerComponent().getAuthFetch();

		const computeHealth = (available: boolean, mustHeartbeatBy: string | null): { state: string; key: string } => {
			if (!available || !mustHeartbeatBy) {
				return { state: "None", key: "deploymentDetail.runtime.health.none" };
			}
			const deadline = new Date(mustHeartbeatBy).getTime();
			const now = Date.now();
			if (now > deadline) {
				return { state: "Error", key: "deploymentDetail.runtime.health.overdue" };
			}
			if (deadline - now < 24 * 60 * 60 * 1000) {
				return { state: "Warning", key: "deploymentDetail.runtime.health.due" };
			}
			return { state: "Success", key: "deploymentDetail.runtime.health.healthy" };
		};

		try {
			// Fix: run bundle load and fetch in parallel — no serialisation penalty.
			const [bundle, r] = await Promise.all([
				this.getResourceBundle(),
				fetcher.json<{
					available: boolean;
					outcome: string;
					lastHeartbeatAt: string | null;
					mustHeartbeatBy: string | null;
				}>(`/api/v1/deployments/${deploymentId}/runtime-status`)
			]);

			const { state, key } = computeHealth(r.available, r.mustHeartbeatBy);
			view.setProperty("/runtime", {
				loaded: true,
				available: r.available,
				outcome: r.outcome,
				lastHeartbeatAt: r.lastHeartbeatAt,
				mustHeartbeatBy: r.mustHeartbeatBy,
				healthState: state,
				healthText: bundle.getText(key) ?? key
			});
		} catch {
			// Graceful degradation — sekcja Runtime nadal pokazuje pola lokalne z OData;
			// MessageStrip "live niedostępne" pojawia się gdy loaded=true && available=false.
			// Bundle may not be assigned if Promise.all rejected — fetch it separately.
			const bundle = await this.getResourceBundle();
			const { key } = computeHealth(false, null);
			view.setProperty("/runtime", {
				loaded: true,
				available: false,
				outcome: "Unavailable",
				lastHeartbeatAt: null,
				mustHeartbeatBy: null,
				healthState: "None",
				healthText: bundle.getText(key) ?? key
			});
		}
	}

	private async loadLicenses(deploymentId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			// Voided and Unbound licenses no longer occupy the deployment. Without the filter
			// `buildLicenseGroups` would count them into totalSeats, so an unbound InPortal seat
			// would still show as a seat of its pool.
			const data = await fetcher.json<{ value: (Omit<LicenseSummary, "LicensingMode"> & { LicensingMode?: string | null })[] }>(
				`/odata/v4/Licenses?$filter=DeploymentId%20eq%20${deploymentId}%20and%20Status%20ne%20'Voided'%20and%20Status%20ne%20'Unbound'&$orderby=CreatedAt%20desc&$select=Id,EntitlementId,ProductName,LicensingMode,LicenseType,Quantity,Status,ValidFrom,ValidUntil`,
				{ headers: { Accept: "application/json" } }
			);
			// LicensingMode comes from the license feed itself (card 50), so splitting pools from license
			// files does not depend on entitlements.read; a null (no product found) counts as unknown.
			const licenses = (Array.isArray(data.value) ? data.value : [])
				.map((l): LicenseSummary => ({ ...l, LicensingMode: l.LicensingMode ?? undefined }));

			await this.enrichWithEntitlementInfo(licenses);
			const assignmentsByLicenseId = await this.enrichWithAssignmentInfo(licenses);

			view.setProperty("/licenses", licenses);
			const oop = licenses.filter(l => l.LicensingMode === "OutOfPortal");
			view.setProperty("/licensesOOP", oop);
			view.setProperty("/hasOutOfPortal", oop.length > 0);
			const groups = buildLicenseGroups(licenses, assignmentsByLicenseId);
			view.setProperty("/licenseGroupsInPortal", groups);
			console.log("[DeploymentDetail] licenses=%d (OOP=%d) → InPortal groups=%d",
				licenses.length, oop.length, groups.length, groups);

			const active = licenses.filter(l => l.Status === "Active").length;
			view.setProperty("/kpi/activeLicenses", String(active));
		} catch {
			view.setProperty("/licenses", []);
			view.setProperty("/licensesOOP", []);
			view.setProperty("/licenseGroupsInPortal", []);
			view.setProperty("/hasOutOfPortal", false);
		}
	}

	/**
	 * ADR-014 Slice 6: dla każdej Active licencji ciągniemy aktywne LicenseAssignment + dane usera.
	 *
	 * Filtr po samym <c>Status === "Active"</c> (bez <c>LicensingMode</c>) — bo enrichment
	 * z EntitlementReports może paść (500) i wtedy <c>LicensingMode</c> jest undefined.
	 * Backend i tak nie zwróci assignments dla OOP-Active (assignments istnieją tylko dla
	 * InPortal), więc dodatkowe puste sub-requesty są bez kosztu.
	 *
	 * Zwraca mapę <c>licenseId → AssignmentDetails</c> używaną przez <c>buildLicenseGroups</c>
	 * do rozbicia listy assignments per grupa (Product × LicenseType).
	 */
	private async enrichWithAssignmentInfo(licenses: LicenseSummary[]): Promise<Map<string, AssignmentDetails>> {
		const byLicenseId = new Map<string, AssignmentDetails>();
		const activeIds = licenses
			.filter(l => l.Status === "Active")
			.map(l => l.Id);
		if (activeIds.length === 0) {
			return byLicenseId;
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const filter = activeIds.map(id => `LicenseId eq ${id}`).join(" or ");
		try {
			const data = await fetcher.json<{ value: {
				Id: string;
				LicenseId: string;
				AssignedUserId: string;
				UserEmail: string;
				UserFirstName: string;
				UserLastName: string;
				AssignedAt: string;
			}[] }>(
				`/odata/v4/LicenseAssignments?$filter=Status%20eq%20'Active'%20and%20(${encodeURIComponent(filter)})&$select=Id,LicenseId,AssignedUserId,UserEmail,UserFirstName,UserLastName,AssignedAt`,
				{ headers: { Accept: "application/json" } }
			);
			for (const a of data.value ?? []) {
				byLicenseId.set(a.LicenseId, {
					assignmentId: a.Id,
					assignedUserId: a.AssignedUserId,
					userEmail: a.UserEmail,
					userFirstName: a.UserFirstName,
					userLastName: a.UserLastName,
					assignedAt: a.AssignedAt,
				});
			}
			for (const l of licenses) {
				const a = byLicenseId.get(l.Id);
				if (a) {
					l.AssignedUserId = a.assignedUserId;
					l.AssignedUserEmail = a.userEmail;
				}
			}
		} catch {
			// silent — UI pokaże "—" gdy brak enrichmentu
		}
		return byLicenseId;
	}

	private async enrichWithEntitlementInfo(licenses: LicenseSummary[]): Promise<void> {
		// ADR-014 diagnostic: log raw przed enrichmentem żeby zobaczyć jaki shape ma Status/LicensingMode.
		console.log("[DeploymentDetail] licenses raw before enrichment:", JSON.parse(JSON.stringify(licenses)));
		const distinctIds = Array.from(new Set(licenses.map(l => l.EntitlementId).filter(Boolean)));
		// EntitlementReports is behind entitlements.read and only adds product code and billing mode here;
		// product name and licensing mode already come from the Licenses feed. Without the permission the
		// pools are grouped by entitlement (buildLicenseGroups) instead of by product.
		if (distinctIds.length === 0 || !this.getOwnerComponent().getPermissions().has("entitlements.read")) {
			return;
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const filter = distinctIds.map(id => `Id eq ${id}`).join(" or ");
		try {
			const data = await fetcher.json<{ value: EntitlementReportSummary[] }>(
				`/odata/v4/EntitlementReports?$filter=${encodeURIComponent(filter)}&$select=Id,ProductId,ProductCode,ProductName,BillingMode`,
				{ headers: { Accept: "application/json" } }
			);
			const byId = new Map<string, EntitlementReportSummary>();
			for (const e of data.value ?? []) {
				byId.set(e.Id, e);
			}
			for (const l of licenses) {
				const e = byId.get(l.EntitlementId);
				if (e) {
					l.ProductId = e.ProductId;
					l.ProductName = e.ProductName;
					l.ProductCode = e.ProductCode;
					l.BillingMode = e.BillingMode;
				}
			}
			console.log("[DeploymentDetail] licenses AFTER enrichment:", JSON.parse(JSON.stringify(licenses)));
		} catch (err) {
			console.warn(
				"[DeploymentDetail] enrichWithEntitlementInfo failed (distinct entitlements=%d, url=%s):",
				distinctIds.length,
				`/odata/v4/EntitlementReports?$filter=${filter}&$select=...`,
				err);
			// Brak enrichmentu — buildLicenseGroups użyje EntitlementId fallback (zobacz ent:<short>)
		}
	}

	public onNavBack(): void {
		this.navTo("deployments", {});
	}

	public onRefresh(): void {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		if (id) {
			this.getView().getModel("odata")?.refresh();
			void this.loadLicenses(id);
		}
	}

	/**
	 * Polls the deployment binding until <c>isDone()</c> returns true or attempts run out.
	 * Reuses the same refresh logic as <c>onRefresh()</c> — refreshes the OData model and
	 * reloads licenses on every tick. Guards against view destruction (post-navigation).
	 *
	 * @param isDone  Optional predicate; polling stops early when it returns true.
	 * @param attempts  Maximum number of refresh attempts (default 6).
	 * @param intervalMs  Wait between attempts in milliseconds (default 2500).
	 */
	private async pollDeploymentRefresh(isDone?: () => boolean, attempts = 6, intervalMs = 2500): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		if (!id) {
			return;
		}
		for (let i = 0; i < attempts; i++) {
			await new Promise<void>((resolve) => { setTimeout(resolve, intervalMs); });
			// Guard: view may be gone if the user navigated away mid-poll.
			if (!this.getView()) {
				return;
			}
			this.getView().getModel("odata")?.refresh();
			await this.loadLicenses(id);
			if (isDone?.()) {
				return;
			}
		}
	}

	public onOpenOwner(): void {
		const view = this.getView().getModel("view") as JSONModel;
		const ownerId = view.getProperty("/ownerId") as string;
		if (ownerId) {
			this.navToOrganization(ownerId);
		}
	}

	// === Card 50: change deployment owner ===

	public async onChangeOwnerPress(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const bundle = await this.getResourceBundle();
		const candidates = await this.loadOwnerCandidates(view.getProperty("/ownerId") as string);
		if (candidates === null) {
			MessageBox.error(bundle.getText("deploymentDetail.owner.loadUsersFailed") ?? "Could not load the users to choose from.");
			return;
		}
		const none: OwnerCandidate = {
			key: NO_OWNER_KEY,
			text: bundle.getText("deploymentDetail.owner.none") ?? "No owner",
			additionalText: ""
		};
		const currentOwnerId = view.getProperty("/ownerUserId") as string;
		// The creation stamp may name a user the server would not accept as a new owner (a partner
		// admin who created a customer deployment). Such an owner is still listed and preselected, so
		// opening the dialog and pressing Save cannot clear it without the admin choosing to.
		if (currentOwnerId && !candidates.some(c => c.key === currentOwnerId)) {
			const displayName = (view.getProperty("/ownerUserDisplayName") as string) || currentOwnerId;
			candidates.unshift({
				key: currentOwnerId,
				text: bundle.getText("deploymentDetail.owner.notEligible", [displayName]) ?? `${displayName} (not eligible)`,
				additionalText: ""
			});
		}
		const currentKey = currentOwnerId || NO_OWNER_KEY;
		view.setProperty("/changeOwner", {
			candidates: [none, ...candidates],
			selectedKey: currentKey,
			currentKey,
			busy: false
		});

		if (!this.changeOwnerDialog) {
			this.changeOwnerDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ChangeOwnerDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.changeOwnerDialog);
		}
		this.changeOwnerDialog.open();
	}

	/**
	 * Active portal users of the deployment's organization and - for a CompuTec caller, whose own
	 * organization is CompuTec - of CompuTec, except developers for a deployment of another organization
	 * (they see CompuTec deployments only). Mirrors the server rule behind `deployment.owner_user_invalid`;
	 * a license-only user carries no role. Null when a list failed.
	 */
	private async loadOwnerCandidates(ownerOrgId: string): Promise<OwnerCandidate[] | null> {
		const orgIds = new Set<string>();
		if (ownerOrgId) {
			orgIds.add(ownerOrgId);
		}
		if (this.getOwnerComponent().getPermissions().organizationType() === "CompuTec") {
			const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
			const ownOrgId = (userModel?.getProperty("/orgId") as string | undefined) ?? "";
			if (ownOrgId) {
				orgIds.add(ownOrgId);
			}
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const lists = await Promise.all([...orgIds].map(async orgId => {
				const rows = await fetcher.json<OrganizationUserRow[]>(`/api/v1/organizations/${orgId}/users`);
				return orgId === ownerOrgId ? rows : rows.filter(u => u.role !== "Developer");
			}));
			return lists.flat()
				.filter(u => u.status === "Active" && u.role !== null)
				.map(u => ({ key: u.id, text: `${u.firstName} ${u.lastName}`.trim(), additionalText: u.email }))
				.sort((a, b) => a.text.localeCompare(b.text));
		} catch (err) {
			console.warn("[DeploymentDetail] owner candidates failed to load", err);
			return null;
		}
	}

	public onCancelChangeOwner(): void {
		this.changeOwnerDialog?.close();
	}

	public async onSubmitChangeOwner(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		const selectedKey = view.getProperty("/changeOwner/selectedKey") as string;
		const bundle = await this.getResourceBundle();
		view.setProperty("/changeOwner/busy", true);
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/deployments/${id}/set-owner`, {
				method: "POST",
				body: JSON.stringify({ ownerUserId: selectedKey === NO_OWNER_KEY ? null : selectedKey })
			});
			this.changeOwnerDialog?.close();
			MessageToast.show(bundle.getText("deploymentDetail.owner.success") ?? "Deployment owner changed.");
			this.getView().getModel("odata")?.refresh();
			void this.loadOwner(id);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deploymentDetail.owner.failure") ?? "Failed to change the deployment owner."));
		} finally {
			view.setProperty("/changeOwner/busy", false);
		}
	}

	// === ADR-014 Slice 6: Assign / Detach InPortal License ===

	private assignLicenseDialog?: Dialog;

	/**
	 * Assign user do grupy seat-poolowej — bierze pierwszy <c>emptySeatLicenseIds[0]</c>,
	 * otwiera <c>AssignLicenseDialog</c> z label-em "ProductCode — LicenseType (N free)".
	 * Backend constraint: <c>AssignUserToLicenseCommand.cs:64</c> i tak rzuci jeśli ktoś
	 * inny przed nami zajął seat — ten race-condition jest akceptowalny (rzadki, użytkownik
	 * zobaczy error w MessageBox i kliknie ponownie).
	 */
	public async onAssignToGroup(event: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const emptySeatIds = (ctx.getProperty("emptySeatLicenseIds") as string[] | undefined) ?? [];
		if (emptySeatIds.length === 0) {
			const bundle = await this.getResourceBundle();
			MessageBox.warning(bundle.getText("deploymentDetail.licensesPool.noFreeSeats") ?? "No free seats.");
			return;
		}
		const licenseId = emptySeatIds[0];
		const productCode = (ctx.getProperty("productCode") as string) || "";
		const licenseType = (ctx.getProperty("licenseType") as string) || "";
		const freeSeats = (ctx.getProperty("freeSeats") as number) || 0;
		const groupKey = (ctx.getProperty("key") as string) || "";
		const bundle = await this.getResourceBundle();
		const label = bundle.getText("deploymentDetail.licensesPool.dialogLabel",
			[productCode, licenseType, String(freeSeats)]) ?? `${productCode} — ${licenseType} (${freeSeats} free)`;

		const view = this.getView().getModel("view") as JSONModel;
		const ownerOrgId = view.getProperty("/ownerId") as string | undefined;
		view.setProperty("/assignLicenseId", licenseId);
		view.setProperty("/assignLicenseLabel", label);
		view.setProperty("/assignGroupKey", groupKey);
		view.setProperty("/assignSelectedUserId", "");

		if (ownerOrgId) {
			await this.loadAssignableUsers(ownerOrgId);
		} else {
			view.setProperty("/assignableUsers", []);
		}

		if (!this.assignLicenseDialog) {
			this.assignLicenseDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AssignLicenseDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.assignLicenseDialog);
		}
		this.assignLicenseDialog.open();
	}

	/**
	 * Release usera z konkretnego assignment-u w grupie. Wzywa istniejący detach endpoint
	 * z licenseId pochodzącym z assignment-u (LicenseAssignment.LicenseId).
	 */
	public async onReleaseAssignment(event: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const licenseId = ctx.getProperty("licenseId") as string;
		const userEmail = (ctx.getProperty("userEmail") as string) || "";
		const bundle = await this.getResourceBundle();
		MessageBox.confirm(
			(bundle.getText("licenses.detach.confirm", [userEmail]) ?? `Release ${userEmail}?`),
			{
				title: bundle.getText("licenses.detach.confirm.title") ?? "Release user",
				onClose: (action: string) => {
					if (action !== "OK") {
						return;
					}
					void this.callDetach(licenseId);
				}
			}
		);
	}

	private async callDetach(licenseId: string): Promise<void> {
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/licenses/${licenseId}/detach`, { method: "POST", body: JSON.stringify({}) });
			MessageToast.show(bundle.getText("licenses.detach.success") ?? "Released.");
			const view = this.getView().getModel("view") as JSONModel;
			void this.loadLicenses(view.getProperty("/deploymentId") as string);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("licenses.detach.failure") ?? "Failed."));
		}
	}

	public async onAssignLicense(event: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const licenseId = ctx.getProperty("Id") as string;
		const productName = (ctx.getProperty("ProductName") as string | undefined) ?? "";
		const view = this.getView().getModel("view") as JSONModel;
		const ownerOrgId = view.getProperty("/ownerId") as string | undefined;

		view.setProperty("/assignLicenseId", licenseId);
		view.setProperty("/assignLicenseLabel", productName);
		view.setProperty("/assignSelectedUserId", "");

		// Load assignable users (active, same org as License.OwnerOrgId).
		if (ownerOrgId) {
			await this.loadAssignableUsers(ownerOrgId);
		} else {
			view.setProperty("/assignableUsers", []);
		}

		if (!this.assignLicenseDialog) {
			this.assignLicenseDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AssignLicenseDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.assignLicenseDialog);
		}
		this.assignLicenseDialog.open();
	}

	private async loadAssignableUsers(orgId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		// GET .../users is behind portal-users.read; the assign and invite actions that lead here are
		// gated in the view, this only keeps the call from going out for a caller who would get 403.
		if (!this.getOwnerComponent().getPermissions().has("portal-users.read")) {
			view.setProperty("/assignableUsers", []);
			return;
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			// Reuse REST endpoint used by OrganizationDetail.loadUsers — filtruje per org po stronie backendu.
			const users = await fetcher.json<{ id: string; email: string; firstName: string; lastName: string; status: string }[]>(
				`/api/v1/organizations/${orgId}/users`
			);
			// Wykluczamy tylko Disabled/Removed. Active + Pending pokazujemy — Pending może
			// dotyczyć świeżo zaproszonego license-only usera (nigdy nie zaakceptuje invitation
			// bo nie loguje się do portalu). Backend AssignUserToLicenseCommand i tak finalnie
			// zwaliduje czy user ma poprawny stan (jeśli nie, MessageBox pokaże 422).
			const items: AssignableUser[] = users
				.filter(u => u.status !== "Disabled" && u.status !== "Removed")
				.map(u => ({ id: u.id, email: u.email, firstName: u.firstName, lastName: u.lastName }));
			console.log("[DeploymentDetail] loadAssignableUsers org=%s: fetched=%d, assignable=%d",
				orgId, users.length, items.length, users.map(u => `${u.email}[${u.status}]`));
			view.setProperty("/assignableUsers", items);
			if (items.length > 0) {
				view.setProperty("/assignSelectedUserId", items[0].id);
			}
		} catch (err) {
			console.warn("[DeploymentDetail] loadAssignableUsers failed for org=%s:", orgId, err);
			view.setProperty("/assignableUsers", []);
		}
	}

	public onCancelAssignLicense(): void {
		this.assignLicenseDialog?.close();
	}

	public async onSubmitAssignLicense(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const licenseId = view.getProperty("/assignLicenseId") as string;
		const userId = view.getProperty("/assignSelectedUserId") as string;
		const bundle = await this.getResourceBundle();

		if (!licenseId || !userId) {
			MessageBox.warning(bundle.getText("licenses.assign.error.userRequired") ?? "Wybierz użytkownika.");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/licenses/${licenseId}/assign`, {
				method: "POST",
				body: JSON.stringify({ userId })
			});
			this.assignLicenseDialog?.close();
			MessageToast.show(bundle.getText("licenses.assign.success") ?? "Assigned.");
			void this.loadLicenses(view.getProperty("/deploymentId") as string);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("licenses.assign.failure") ?? "Failed to assign."));
		}
	}

	public async onDetachLicense(event: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const licenseId = ctx.getProperty("Id") as string;
		const userEmail = (ctx.getProperty("AssignedUserEmail") as string | undefined) ?? "";
		const bundle = await this.getResourceBundle();

		MessageBox.confirm(
			(bundle.getText("licenses.detach.confirm", [userEmail]) ?? `Detach ${userEmail}?`),
			{
				title: bundle.getText("licenses.detach.confirm.title") ?? "Detach user",
				onClose: async (action: string) => {
					// MessageBox.Action.OK = "OK" — porównujemy do stringa, żeby uniknąć
					// @typescript-eslint/no-unsafe-enum-comparison.
					if (action !== "OK") {
						return;
					}
					try {
						const fetcher = this.getOwnerComponent().getAuthFetch();
						await fetcher.json(`/api/v1/licenses/${licenseId}/detach`, {
							method: "POST",
							body: JSON.stringify({})
						});
						MessageToast.show(bundle.getText("licenses.detach.success") ?? "Detached.");
						const view = this.getView().getModel("view") as JSONModel;
						void this.loadLicenses(view.getProperty("/deploymentId") as string);
					} catch (e) {
						const err = e as ResponseError;
						MessageBox.error(problemMessage(err.bodyText, bundle.getText("licenses.detach.failure") ?? "Failed to detach."));
					}
				}
			}
		);
	}

	public onBindLicenses(): void {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		if (id) {
			this.navTo("deploymentBindLicenses", { id });
		}
	}

	// === Issue enrollment code ===

	public async onIssueCodePress(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		if (!id) {
			return;
		}
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const result = await fetcher.json<IssueEnrollmentCodeResponse>(
				`/api/v1/deployments/${id}/enrollment-code`,
				{ method: "POST" });

			await this.showEnrollmentCode(result);
			MessageToast.show(bundle.getText("deployments.code.success") ?? "Enrollment code issued");
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.code.failure") ?? "Failed"));
		}
	}

	/**
	 * Jednorazowa prezentacja plaintext kodu (enroll i re-enroll) — kod nie jest
	 * przechowywany po stronie portalu, więc to jedyny moment kiedy user może go skopiować.
	 */
	private async showEnrollmentCode(result: IssueEnrollmentCodeResponse): Promise<void> {
		if (!this.codeDialog) {
			this.codeDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.EnrollmentCodeDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.codeDialog);
		}
		(this.byId("taEnrollmentCode") as TextArea).setValue(result.code);
		(this.byId("txtEnrollmentExpires") as Text).setText(new Date(result.expiresAt).toLocaleString());
		this.codeDialog.open();
	}

	public onCloseEnrollmentCode(): void {
		this.codeDialog?.close();
	}

	// === Re-enroll ===

	public async onReEnrollPress(): Promise<void> {
		if (!this.reEnrollDialog) {
			this.reEnrollDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ReEnrollDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.reEnrollDialog);
		}
		(this.byId("selReEnrollReason") as Select).setSelectedKey("RoutineRotation");
		this.reEnrollDialog.open();
	}

	public onCancelReEnroll(): void {
		this.reEnrollDialog?.close();
	}

	public async onConfirmReEnroll(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		// Wartość ReEnrollmentReason (kontrakt LR) — backend odrzuca wolny tekst (400).
		const reason = (this.byId("selReEnrollReason") as Select).getSelectedKey();
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const result = await fetcher.json<IssueEnrollmentCodeResponse>(
				`/api/v1/deployments/${id}/re-enroll`,
				{
					method: "POST",
					body: JSON.stringify({ reason })
				});
			this.reEnrollDialog?.close();
			await this.showEnrollmentCode(result);
			MessageToast.show(bundle.getText("deployments.reenroll.success") ?? "Re-enroll requested");
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.reenroll.failure") ?? "Failed"));
		}
	}

	// === Reset (reaktywacja) — ADR-024 ===

	/**
	 * Otwiera ResetDialog. Reset to wysokouprzywilejowana operacja recovery (widoczna tylko
	 * dla roli serwisowej z uprawnieniem <c>deployments.reset</c>) — reaktywuje deployment
	 * z powrotem do PendingEnrollment zachowując Id/licencje/powiązania/historię. Poprzednia
	 * instalacja zostaje unieważniona (superseded). Dialog zawiera mocne ostrzeżenie + reason Select.
	 */
	public async onResetPress(): Promise<void> {
		if (!this.resetDialog) {
			this.resetDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ResetDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.resetDialog);
		}
		(this.byId("selResetReason") as Select).setSelectedKey("TotalLoss");
		this.resetDialog.open();
	}

	public onCancelReset(): void {
		this.resetDialog?.close();
	}

	public async onConfirmReset(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		// Wartość ResetReason (kontrakt LR, ADR-024) — backend odrzuca wolny tekst (400).
		const reason = (this.byId("selResetReason") as Select).getSelectedKey();
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(
				`/api/v1/deployments/${id}/reset`,
				{
					method: "POST",
					body: JSON.stringify({ reason })
				});
			this.resetDialog?.close();
			MessageToast.show(bundle.getText("deployments.reset.success") ?? "Deployment reset");
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.reset.failure") ?? "Could not reset deployment"));
		}
	}

	/**
	 * Re-sync (re-register) the deployment with the License Runtime — ADR-024 follow-up. Re-emits an
	 * idempotent deployment.created for the recovery case where LR never learned about the deployment
	 * (legacy deployment predating the LR outbox wiring, or a stuck PendingEnrollment where reset is a
	 * no-op). Service permission (deployments.reset). No state change.
	 */
	public async onLrResyncPress(): Promise<void> {
		const bundle = await this.getResourceBundle();
		MessageBox.confirm(
			bundle.getText("deployments.lrResync.confirm") ?? "Re-register this deployment with the License Runtime?",
			{
				onClose: async (action: unknown) => {
					if (action !== MessageBox.Action.OK) {
						return;
					}
					const view = this.getView().getModel("view") as JSONModel;
					const id = view.getProperty("/deploymentId") as string;
					try {
						const fetcher = this.getOwnerComponent().getAuthFetch();
						await fetcher.json(`/api/v1/deployments/${id}/lr-resync`, { method: "POST" });
						MessageToast.show(bundle.getText("deployments.lrResync.success") ?? "Re-sync requested");
						this.onRefresh();
					} catch (e) {
						const err = e as ResponseError;
						MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.lrResync.failure") ?? "Could not re-sync deployment"));
					}
				}
			});
	}

	// === Offline enroll (air-gapped courier flow) ===

	public async onOfflineEnrollPress(): Promise<void> {
		if (!this.offlineEnrollDialog) {
			this.offlineEnrollDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.OfflineEnrollDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.offlineEnrollDialog);
		}
		(this.byId("fuOfflineEnroll") as FileUploader).clear();
		this.offlineEnrollDialog.open();
	}

	public onCancelOfflineEnroll(): void {
		this.offlineEnrollDialog?.close();
	}

	public async onConfirmOfflineEnroll(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const fu = this.byId("fuOfflineEnroll") as FileUploader;
		const domRef = fu.getDomRef();
		const fileInput = domRef?.querySelector<HTMLInputElement>("input[type='file']");
		const file = fileInput?.files?.[0];
		if (!file) {
			MessageBox.warning(bundle.getText("deployments.offline.enroll.fileRequired") ?? "Select a request file.");
			return;
		}
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		try {
			const body = await file.text();
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const response = await fetcher.fetch(`/api/v1/deployments/${id}/offline-enrollment`, {
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" }
			});
			if (!response.ok) {
				const errText = await response.text();
				throw new OfflineApiError(response.status, errText);
			}
			const responseText = await response.text();
			triggerFileDownload(responseText, `offline-enrollment-response-${id}.json`);
			this.offlineEnrollDialog?.close();
			MessageToast.show(bundle.getText("deployments.offline.enroll.inProgress") ?? "Offline enrollment submitted — deployment status will update shortly.");
			void this.pollDeploymentRefresh(() => {
				const ctx = this.getView().getBindingContext("odata");
				const status = ctx?.getProperty("Status") as string | undefined;
				return status === "Active";
			});
		} catch (e) {
			if (e instanceof OfflineApiError) {
				MessageBox.error(parseOfflineError(e, bundle.getText("deployments.offline.enroll.failure") ?? "Offline enrollment failed"));
			} else {
				const err = e as ResponseError;
				MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.offline.enroll.failure") ?? "Offline enrollment failed"));
			}
		}
	}

	// === Offline refresh (air-gapped courier flow) ===

	public async onOfflineRefreshPress(): Promise<void> {
		if (!this.offlineRefreshDialog) {
			this.offlineRefreshDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.OfflineRefreshDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.offlineRefreshDialog);
		}
		(this.byId("fuOfflineRefresh") as FileUploader).clear();
		this.offlineRefreshDialog.open();
	}

	public onCancelOfflineRefresh(): void {
		this.offlineRefreshDialog?.close();
	}

	public async onConfirmOfflineRefresh(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const fu = this.byId("fuOfflineRefresh") as FileUploader;
		const domRef = fu.getDomRef();
		const fileInput = domRef?.querySelector<HTMLInputElement>("input[type='file']");
		const file = fileInput?.files?.[0];
		if (!file) {
			MessageBox.warning(bundle.getText("deployments.offline.refresh.fileRequired") ?? "Select a request file.");
			return;
		}
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		try {
			const body = await file.text();
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const response = await fetcher.fetch(`/api/v1/deployments/${id}/offline-refresh`, {
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" }
			});
			if (!response.ok) {
				const errText = await response.text();
				throw new OfflineApiError(response.status, errText);
			}
			const responseText = await response.text();
			triggerFileDownload(responseText, `offline-refresh-response-${id}.json`);
			this.offlineRefreshDialog?.close();
			MessageToast.show(bundle.getText("deployments.offline.refresh.inProgress") ?? "Offline refresh submitted — license file version will update shortly.");
			void this.pollDeploymentRefresh();
		} catch (e) {
			if (e instanceof OfflineApiError) {
				MessageBox.error(parseOfflineError(e, bundle.getText("deployments.offline.refresh.failure") ?? "Offline refresh failed"));
			} else {
				const err = e as ResponseError;
				MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.offline.refresh.failure") ?? "Offline refresh failed"));
			}
		}
	}

	// === Generuj offline enrollment bundle (initiator Portal→AE) ===

	public async onGenerateOfflineBundlePress(): Promise<void> {
		if (!this.offlineBundleDialog) {
			this.offlineBundleDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.OfflineBundleDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.offlineBundleDialog);
		}
		(this.byId("inpOfflineBundleCode") as Input).setValue("");
		this.offlineBundleDialog.open();
	}

	public onCancelGenerateOfflineBundle(): void {
		this.offlineBundleDialog?.close();
	}

	public async onConfirmGenerateOfflineBundle(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const code = ((this.byId("inpOfflineBundleCode") as Input).getValue() ?? "").trim();
		if (!code) {
			MessageBox.warning(bundle.getText("deployments.offline.bundle.codeRequired") ?? "Enter the enrollment code.");
			return;
		}
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/deploymentId") as string;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const response = await fetcher.fetch(`/api/v1/deployments/${id}/offline-enrollment-bundle`, {
				method: "POST",
				body: JSON.stringify({ enrollmentCode: code }),
				headers: { "Content-Type": "application/json" }
			});
			if (!response.ok) {
				const errText = await response.text();
				throw new OfflineApiError(response.status, errText);
			}
			const responseText = await response.text();
			triggerFileDownload(responseText, `offline-enrollment-bundle-${id}.json`);
			this.offlineBundleDialog?.close();
			MessageToast.show(bundle.getText("deployments.offline.bundle.success") ?? "Offline bundle generated — transfer it to the air-gapped AppEngine.");
		} catch (e) {
			if (e instanceof OfflineApiError) {
				MessageBox.error(parseOfflineError(e, bundle.getText("deployments.offline.bundle.failure") ?? "Failed to generate offline bundle"));
			} else {
				const err = e as ResponseError;
				MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.offline.bundle.failure") ?? "Failed to generate offline bundle"));
			}
		}
	}

	// === Invite user do customera deploymentu (reużywa InviteUserDialog fragment z PortalUsers feature) ===

	private inviteUserDialog?: Dialog;

	/**
	 * Otwiera InviteUserDialog z pre-fillem na OrgId = deployment.OwnerOrgId. Default
	 * <c>HasPortalAccess=false</c> bo z perspektywy DeploymentDetail najczęściej invituje się
	 * license-only userów (do natychmiastowego przypisania do seatów). User może przełączyć
	 * Switch jeśli chce dodać portal-usera. Org picker jest ukryty (<c>inviteShowOrgPicker=false</c>).
	 *
	 * Gdy wywołane z kontekstu LicenseGroup (przycisk w headerToolbar puli) — bindingContext "view"
	 * niesie <c>LicenseGroup</c>; przekazujemy <c>entitlementId</c> jako auto-assignment intent.
	 * Po accept invite backend automatycznie przypisze usera do pierwszego wolnego seata w tej puli.
	 */
	public async onInviteUserToCustomer(event?: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const ownerId = view.getProperty("/ownerId") as string;
		if (!ownerId) {
			const bundle = await this.getResourceBundle();
			MessageBox.warning(bundle.getText("deploymentDetail.invite.noOwner") ?? "Owner organization unknown.");
			return;
		}

		const groupCtx = event?.getSource().getBindingContext("view") ?? null;
		const intendedEntitlementId = (groupCtx?.getProperty("entitlementId") as string | undefined) ?? "";
		const intendedProductCode = (groupCtx?.getProperty("productCode") as string | undefined) ?? "";
		const intendedProductName = (groupCtx?.getProperty("productName") as string | undefined) ?? "";
		const intendedLicenseType = (groupCtx?.getProperty("licenseType") as string | undefined) ?? "";
		const intendedLabel = intendedEntitlementId
			? `${intendedProductCode} — ${intendedProductName} (${intendedLicenseType})`.trim()
			: "";

		view.setProperty("/inviteShowOrgPicker", false);
		view.setProperty("/inviteOrgId", ownerId);
		view.setProperty("/inviteOrgName", view.getProperty("/ownerName") as string);
		view.setProperty("/inviteHasPortalAccess", false);
		view.setProperty("/inviteRoleIsUser", true);
		view.setProperty("/inviteIntendedEntitlementId", intendedEntitlementId);
		view.setProperty("/inviteIntendedEntitlementLabel", intendedLabel);
		view.setProperty("/inviteValueState", { ...INVITE_VALUE_STATE_NONE });

		if (!this.inviteUserDialog) {
			this.inviteUserDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.InviteUserDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.inviteUserDialog);
		}

		// Reset pól na każde otwarcie.
		(this.byId("inpInviteEmail") as Input).setValue("");
		(this.byId("inpInviteFirstName") as Input).setValue("");
		(this.byId("inpInviteLastName") as Input).setValue("");
		(this.byId("ddInviteRole") as Select).setSelectedKey("User");

		this.inviteUserDialog.open();
	}

	public onCancelInviteUser(): void {
		this.inviteUserDialog?.close();
	}

	public onInviteRoleChange(): void {
		const role = (this.byId("ddInviteRole") as Select).getSelectedKey();
		(this.getView().getModel("view") as JSONModel).setProperty("/inviteRoleIsUser", role === "User");
	}

	public onInvitePortalAccessChange(): void {
		// State zaktualizowany przez two-way binding na Switch.state — fragment ma
		// visible="{view>/inviteHasPortalAccess}" na Role/Profile.
	}

	public async onSaveInviteUser(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;

		const orgId = (view.getProperty("/inviteOrgId") as string).trim();
		const email = (this.byId("inpInviteEmail") as Input).getValue().trim();
		const firstName = (this.byId("inpInviteFirstName") as Input).getValue().trim();
		const lastName = (this.byId("inpInviteLastName") as Input).getValue().trim();
		const hasPortalAccess = view.getProperty("/inviteHasPortalAccess") as boolean;
		const role = (this.byId("ddInviteRole") as Select).getSelectedKey() as "User" | "Admin" | "Auditor" | "MarketingManager" | "Developer";

		if (orgId.length === 0 || email.length === 0 || firstName.length === 0 || lastName.length === 0) {
			MessageBox.warning(
				bundle.getText("organization.detail.users.dialog.error.fieldsRequired") ?? "Wypełnij wszystkie wymagane pola.");
			return;
		}

		const intendedEntitlementId = (view.getProperty("/inviteIntendedEntitlementId") as string).trim();
		const payload = {
			email,
			firstName,
			lastName,
			hasPortalAccess,
			role: hasPortalAccess ? role : null,
			intendedEntitlementId: intendedEntitlementId.length > 0 ? intendedEntitlementId : null
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const response = await fetcher.json<InviteUserResponse>(
				`/api/v1/organizations/${orgId}/users/invite`,
				{ method: "POST", body: JSON.stringify(payload) }
			);
			this.inviteUserDialog?.close();
			showInviteOutcome(bundle, response);

			// Po invite — odśwież assignable users z customera (nowy user pojawi się w pickerze
			// po następnym otwarciu Assign dialogu).
			await this.loadAssignableUsers(orgId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.detail.users.dialog.failure") ?? "Nie udało się zaprosić użytkownika."));
		}
	}

}

interface AssignmentDetails {
	assignmentId: string;
	assignedUserId: string;
	userEmail: string;
	userFirstName: string;
	userLastName: string;
	assignedAt: string;
}

/**
 * Buduje pule seatów InPortal grupując licencje po (ProductId, LicenseType).
 * Wyrzuca OutOfPortal — te lecą do osobnej flat tabeli.
 *
 * Fallback gdy <c>ProductId</c> brakuje (enrichment z EntitlementReports padł — np. 500):
 * grupujemy po <c>EntitlementId</c> i pokazujemy short-id jako productCode. Lepsze niż
 * silent-drop, bo user widzi że są licencje (z licznikiem) i może zgłosić problem
 * z enrichmentem konkretnie. <strong>Założenie</strong>: <c>LicensingMode</c> też jest
 * z enrichment-u; jeśli ono padło to nie wiemy czy to InPortal — wtedy fallback: licencja
 * o <c>Status=EmptySeat</c> jest per-domain ZAWSZE InPortal (License.Create wymusza),
 * więc traktujemy EmptySeat jako proxy dla InPortal gdy LicensingMode undefined.
 */
function buildLicenseGroups(
	licenses: LicenseSummary[],
	assignmentsByLicenseId: Map<string, AssignmentDetails>,
): LicenseGroup[] {
	const groups = new Map<string, LicenseGroup>();

	// Gdy enrichment z EntitlementReports padł, LicensingMode może być undefined dla
	// WSZYSTKICH licencji deploymentu. Pojedyncze EmptySeat to deterministyczny proxy
	// dla InPortal (domain invariant — Status=EmptySeat WYŁĄCZNIE dla InPortal). Jeśli
	// którakolwiek licencja deploymentu ma EmptySeat → cały deployment jest InPortal-only
	// (deployment trzyma licencje z jednego Entitlement-u; nie da się zmieszać In/OOP w jednej
	// puli per Product). W takim razie traktuj wszystkie licencje deploymentu jako InPortal —
	// to pokrywa też Active po assignie (gdzie sam Status="Active" nie odróżnia InPortal od OOP).
	const treatAllAsInPortal = licenses.some(l => l.Status === "EmptySeat")
		&& licenses.every(l => l.LicensingMode === undefined || l.LicensingMode === "InPortal");

	for (const lic of licenses) {
		const isInPortal = lic.LicensingMode === "InPortal"
			|| lic.Status === "EmptySeat"
			|| treatAllAsInPortal;
		if (!isInPortal) {
			continue;
		}
		// Group key: preferuj ProductId; fallback do EntitlementId gdy enrichment padł.
		const groupingId = lic.ProductId || lic.EntitlementId;
		if (!groupingId) {
			continue;
		}
		const key = `${groupingId}|${lic.LicenseType}`;
		let g = groups.get(key);
		if (!g) {
			const shortEntId = lic.EntitlementId.substring(0, 8);
			g = {
				key,
				productId: lic.ProductId ?? "",
				// Without entitlements.read there is no product code; the name from the Licenses feed
				// stands in for it (the pool header then shows the name once) rather than a raw id.
				productCode: lic.ProductCode ?? lic.ProductName ?? `ent:${shortEntId}`,
				productName: lic.ProductName ?? `Entitlement ${shortEntId}…`,
				licenseType: lic.LicenseType,
				licensingMode: lic.LicensingMode ?? "InPortal",
				entitlementId: lic.EntitlementId,
				totalSeats: 0,
				assignedSeats: 0,
				freeSeats: 0,
				emptySeatLicenseIds: [],
				assignments: [],
			};
			groups.set(key, g);
		}
		g.totalSeats += lic.Quantity || 1;
		if (lic.Status === "EmptySeat") {
			g.emptySeatLicenseIds.push(lic.Id);
		}
		const a = assignmentsByLicenseId.get(lic.Id);
		if (a) {
			g.assignedSeats += 1;
			g.assignments.push({
				licenseId: lic.Id,
				assignmentId: a.assignmentId,
				userId: a.assignedUserId,
				userEmail: a.userEmail,
				userFullName: `${a.userFirstName} ${a.userLastName}`.trim(),
				assignedAt: a.assignedAt,
			});
		}
	}

	for (const g of groups.values()) {
		g.freeSeats = g.totalSeats - g.assignedSeats;
		g.assignments.sort((x, y) => x.userEmail.localeCompare(y.userEmail));
	}

	return Array.from(groups.values()).sort((a, b) => {
		const byProduct = a.productCode.localeCompare(b.productCode);
		return byProduct !== 0 ? byProduct : a.licenseType.localeCompare(b.licenseType);
	});
}

/**
 * Slim error type for non-2xx responses in the offline courier flow.
 * Keeps the body text so we can attempt to extract a message field.
 */
class OfflineApiError extends Error {
	public readonly status: number;
	public readonly bodyText: string;

	constructor(status: number, bodyText: string) {
		super(`HTTP ${status}`);
		this.status = status;
		this.bodyText = bodyText;
	}
}

/**
 * Parses a JSON error body from the offline API call and returns a user-readable message.
 * Accepted shapes: `{ message }`, `{ error }`, `{ title }`, `{ detail }`.
 * Falls back to `fallback` when the body is not JSON or none of the fields are present.
 */
function parseOfflineError(err: OfflineApiError, fallback: string): string {
	try {
		const parsed = JSON.parse(err.bodyText) as {
			message?: string;
			error?: string;
			title?: string;
			detail?: string;
		};
		return parsed.message ?? parsed.error ?? parsed.detail ?? parsed.title ?? fallback;
	} catch {
		return err.bodyText.trim() || fallback;
	}
}

/**
 * Triggers a browser download of `content` as a file with the given `filename`.
 * Uses a temporary `<a download>` anchor — no server round-trip.
 */
function triggerFileDownload(content: string, filename: string): void {
	const blob = new Blob([content], { type: "application/json" });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}
